export type FiboProjectionStopTrend = 'long' | 'short'

const DEFAULT_MARGIN_PCT = 0.0025 // 0.25%

function safePow10(exp: number): number {
  if (!Number.isFinite(exp)) return 1
  const e = Math.max(0, Math.min(12, Math.trunc(exp)))
  return 10 ** e
}

function toInt(value: number, scale: number): number | null {
  if (!Number.isFinite(value) || !Number.isFinite(scale) || scale <= 0) return null
  const n = Math.round(value * scale)
  return Number.isFinite(n) ? n : null
}

function clampMarginPct(pct: number): number {
  if (!Number.isFinite(pct)) return DEFAULT_MARGIN_PCT
  return Math.max(0, Math.min(0.05, pct))
}

function displayTickFromAnchor(anchor: number): number {
  const a = Math.abs(anchor)
  return a >= 1 ? 0.0001 : 0.000001
}

function quantizeToTick(value: number, tick: number, mode: 'ceil' | 'floor'): number | null {
  if (!Number.isFinite(value) || value <= 0) return null
  if (!Number.isFinite(tick) || tick <= 0) return null

  const decimals = Math.max(0, Math.round(-Math.log10(tick)))
  const scale = safePow10(decimals)
  const tickInt = toInt(tick, scale)
  const valueInt = toInt(value, scale)
  if (tickInt == null || tickInt <= 0) return null
  if (valueInt == null) return null

  const q = mode === 'ceil' ? Math.ceil(valueInt / tickInt) : Math.floor(valueInt / tickInt)
  const outInt = q * tickInt
  const out = outInt / scale
  return Number.isFinite(out) && out > 0 ? out : null
}

/**
 * Stop por margem percentual a partir do ponto 1 da Fibo (invalidação):
 * - `short`: acima do ponto 1 (hi)  -> `hi * (1 + marginPct)`
 * - `long`:  abaixo do ponto 1 (lo) -> `lo * (1 - marginPct)`
 *
 * Depois, ajusta para um "tick" fixo (apenas para arredondar):
 * - `>= 1`: 0.0001
 * - `< 1`:  0.000001
 */
export function computeFiboProjectionStop(params: {
  hi: number
  lo: number
  trend: FiboProjectionStopTrend
  marginPct?: number
}): number | null {
  const hi = Number(params.hi)
  const lo = Number(params.lo)
  if (!Number.isFinite(hi) || !Number.isFinite(lo) || hi <= lo) return null

  const trend = params.trend
  const anchor = trend === 'short' ? hi : lo
  if (!Number.isFinite(anchor) || anchor <= 0) return null

  const marginPct = clampMarginPct(Number(params.marginPct ?? DEFAULT_MARGIN_PCT))
  const raw = trend === 'short' ? anchor * (1 + marginPct) : anchor * (1 - marginPct)
  if (!Number.isFinite(raw) || raw <= 0) return null

  const tick = displayTickFromAnchor(anchor)
  const stop = quantizeToTick(raw, tick, trend === 'short' ? 'floor' : 'ceil')
  if (stop == null) return null

  if (trend === 'short' && stop <= anchor) {
    const bumped = quantizeToTick(anchor + tick, tick, 'ceil')
    return bumped != null ? bumped : stop
  }
  if (trend === 'long' && stop >= anchor) {
    const bumped = quantizeToTick(anchor - tick, tick, 'floor')
    return bumped != null ? bumped : stop
  }

  return stop
}
